/**
 * iOS Safari Touch Fix Utilities
 * Addresses specific touch interaction issues on iOS Safari
 */

/**
 * Detects if the current browser is iOS Safari
 * @returns {boolean} True if iOS Safari
 */
export const isIOSSafari = () => {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|mercury/.test(ua);
};

/**
 * Applies iOS Safari-specific touch fixes to an element
 * @param {HTMLElement} element - Element to apply fixes to
 */
export const applyIOSSafariTouchFix = (element) => {
  if (!element || !isIOSSafari()) return;
  
  // Use CSS touch-action to prevent zoom
  element.style.touchAction = 'manipulation';
  element.style.webkitTouchCallout = 'none';
  element.style.webkitUserSelect = 'none';
  
  // Add a small delay to prevent iOS Safari touch issues
  let touchStartTime = 0;
  
  const handleTouchStart = (e) => {
    touchStartTime = Date.now();
  };
  
  const handleTouchEnd = (e) => {
    const touchDuration = Date.now() - touchStartTime;
    
    // Only trigger click for quick taps (not long presses)
    if (touchDuration < 300) {
      // Let the natural click event fire
      return;
    } else {
      e.preventDefault();
    }
  };
  
  element.addEventListener('touchstart', handleTouchStart, { passive: true });
  element.addEventListener('touchend', handleTouchEnd, { passive: false });
  
  return () => {
    element.removeEventListener('touchstart', handleTouchStart);
    element.removeEventListener('touchend', handleTouchEnd);
  };
};

/**
 * Creates a touch-friendly click handler for iOS Safari
 * @param {Function} onClick - Original click handler
 * @returns {Object} Event handlers for touch and click
 */
export const createIOSSafariClickHandler = (onClick) => {
  if (!isIOSSafari()) {
    return { onClick };
  }
  
  let touchHandled = false;
  
  const handleTouchEnd = (e) => {
    touchHandled = true;
    onClick(e);
    
    // Reset flag after a short delay
    setTimeout(() => {
      touchHandled = false;
    }, 100);
  };
  
  const handleClick = (e) => {
    // Prevent duplicate events
    if (touchHandled) {
      e.preventDefault();
      return;
    }
    onClick(e);
  };
  
  return {
    onTouchEnd: handleTouchEnd,
    onClick: handleClick
  };
};